import { isCurrentAnalyticsDocumentSensitive } from "./sensitiveDocument";
import { getConsent } from "./consent";
import { clearPixelCookies } from "./pixelCookies";
import type { PostHog } from "posthog-js";

interface AnalyticsRuntime<Client> {
  client: Client;
  initAnalytics(): boolean;
  teardownAnalytics(): void;
}

/** Consent is checked before download and again after it, including withdrawal races. */
export function createLazyAnalytics<Client>({ getConsent: readConsent, load }: {
  getConsent: typeof getConsent;
  load: () => Promise<AnalyticsRuntime<Client>>;
}) {
  let runtime: AnalyticsRuntime<Client> | undefined;
  let loading: Promise<AnalyticsRuntime<Client>> | undefined;
  let client: Client | undefined;
  const listeners = new Set<() => void>();
  const publish = (next: Client | undefined) => {
    client = next;
    listeners.forEach(listener => listener());
  };

  return {
    getClient: () => client,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    async init(): Promise<boolean> {
      if (readConsent() !== "accepted" || isCurrentAnalyticsDocumentSensitive()) return false;
      try {
        loading ??= load();
        runtime = await loading;
        if (readConsent() !== "accepted" || isCurrentAnalyticsDocumentSensitive()) return false;
        const ready = runtime.initAnalytics();
        publish(ready ? runtime.client : undefined);
        return ready;
      } catch {
        loading = undefined;
        return false;
      }
    },
    teardown() {
      // Do not download trackers just to reject them on a first visit.
      publish(undefined);
      runtime?.teardownAnalytics();
      clearPixelCookies();
    },
  };
}

export const marketingAnalytics = createLazyAnalytics<PostHog>({
  getConsent,
  load: () => import("./runtime"),
});
